'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { TrendingUp, TrendingDown, Flame, Trophy, Zap } from 'lucide-react';
import { useLang } from '@/lib/i18n/LanguageContext';

interface HotTicker {
  symbol: string;
  count: number;
  direction: 'up' | 'down' | 'neutral';
  label: string;
}

interface TopTrader {
  userId: string;
  username: string;
  displayName: string;
  initial: string;
  avatarUrl: string | null;
  stmScore: number;
  tier: string | null;
  totalTrades: number;
}

// Solid brand-palette rotation for avatar fallbacks (replaces the old dark-theme gradient set) —
// matches the "Top trejderi" row treatment in the design handoff (solid initials circle, not a gradient).
const AVATAR_COLORS = ['var(--accent)', 'var(--info)', 'var(--bearish)', 'var(--win)', 'var(--bullish)'];

function tierLabel(t: string | null): string {
  return t ? t.charAt(0) + t.slice(1).toLowerCase() : '';
}

// Map /api/leaderboard rows (already eligibleForTop-filtered + stmScore-sorted) to the widget shape.
function mapTraders(arr: any[]): TopTrader[] {
  return (arr || []).slice(0, 3).map((t: any) => ({
    userId: t.userId,
    username: t.username,
    displayName: t.displayName || t.username,
    initial: t.initial || (t.username || 'T')[0].toUpperCase(),
    avatarUrl: t.avatarUrl ?? null,
    stmScore: Number(t.stmScore ?? 0),
    tier: t.tier ?? null,
    totalTrades: t.totalTrades ?? 0,
  }));
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <div className="uppercase" style={{ fontSize: 11, fontWeight: 800, letterSpacing: '1.2px', color: 'var(--text-muted)' }}>
      {children}
    </div>
  );
}

export default function TrendingSidebar() {
  const { t } = useLang();
  const [hotTickers, setHotTickers] = useState<HotTicker[]>([]);
  const [weekTraders, setWeekTraders] = useState<TopTrader[]>([]);
  const [monthTraders, setMonthTraders] = useState<TopTrader[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      try {
        // Top Traders use the SAME source as /leaderboard: category=performance => only traders with enough closed trades in the period (see lib/leaderboard/config), ranked by STM/performance.
        const [trendRes, weekRes, monthRes] = await Promise.all([
          fetch('/api/trending'),
          fetch('/api/leaderboard?period=7d&category=performance'),
          fetch('/api/leaderboard?period=30d&category=performance'),
        ]);
        if (cancelled) return;
        if (trendRes.ok) { const d = await trendRes.json(); setHotTickers(d.hotTickers || []); }
        if (weekRes.ok) { const d = await weekRes.json(); setWeekTraders(mapTraders(d.traders)); }
        if (monthRes.ok) { const d = await monthRes.json(); setMonthTraders(mapTraders(d.traders)); }
      } catch {
        // silently fail
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => { cancelled = true; };
  }, []);

  const emptyTraders = (
    <div className="px-4 py-6 text-center">
      <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('No traders yet')}</p>
      <p className="text-xs mt-1 leading-relaxed" style={{ color: 'var(--text-muted)' }}>
        {t('Post a signal to show up here.')}
      </p>
    </div>
  );

  function renderTraders(list: TopTrader[]) {
    if (loading) {
      return Array.from({ length: 3 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 px-4 py-3">
          <div className="w-4 h-3 rounded skeleton shrink-0" />
          <div className="w-[30px] h-[30px] rounded-full skeleton shrink-0" />
          <div className="flex-1">
            <div className="w-24 h-3 rounded skeleton mb-1" />
            <div className="w-16 h-2.5 rounded skeleton" />
          </div>
          <div className="w-10 h-4 rounded skeleton" />
        </div>
      ));
    }
    if (list.length === 0) return emptyTraders;
    return list.map((trader, i) => {
      const avatarColor = AVATAR_COLORS[i % AVATAR_COLORS.length];
      const rankColor = i === 0 ? 'var(--win)' : 'var(--text-muted)';
      return (
        <Link
          key={trader.userId}
          href={`/profile/${encodeURIComponent(trader.username)}`}
          className="flex items-center gap-3 px-4 py-3 transition-colors duration-100"
          onMouseEnter={(e) => { e.currentTarget.style.background = 'var(--bg-card-hover)'; }}
          onMouseLeave={(e) => { e.currentTarget.style.background = 'transparent'; }}
        >
          <div className="font-mono-nums font-bold text-right shrink-0" style={{ width: 16, color: rankColor }}>{i + 1}</div>
          <div className="relative shrink-0">
            {trader.avatarUrl ? (
              <img src={trader.avatarUrl} alt={trader.displayName} className="rounded-full object-cover" style={{ width: 30, height: 30 }} />
            ) : (
              <div
                className="rounded-full flex items-center justify-center text-xs font-bold"
                style={{ width: 30, height: 30, background: avatarColor, color: 'var(--bg-base)' }}
              >
                {trader.initial}
              </div>
            )}
          </div>
          <div className="flex-1 min-w-0">
            <div className="truncate" style={{ fontSize: '13.5px', fontWeight: 700, color: 'var(--text-primary)' }}>{trader.displayName}</div>
            <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-muted)' }}>
              @{trader.username}{trader.tier ? ` · ${tierLabel(trader.tier)}` : ''} · {trader.totalTrades} {t('trades')}
            </div>
          </div>
          <div className="text-right shrink-0">
            <div className="font-mono-nums font-bold" style={{ fontSize: '12.5px', color: 'var(--bullish)' }}>{trader.stmScore.toFixed(0)}</div>
            <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>STM</div>
          </div>
        </Link>
      );
    });
  }

  return (
    <aside className="flex flex-col gap-4">

      {/* ── TRENDING TICKERS ("Tržišta danas" widget; real trade activity, no fake fallback) ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <Flame size={14} style={{ color: 'var(--win)' }} />
          <SectionLabel>{t('Trending Tickers')}</SectionLabel>
          <span className="ml-auto text-xs px-1.5 py-0.5 rounded-md font-semibold" style={{ background: 'var(--win-muted)', color: 'var(--win)', border: '1px solid transparent' }}>
            {t('Live')}
          </span>
        </div>
        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {loading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-3">
                <div className="w-4 h-3 rounded skeleton" />
                <div className="flex-1">
                  <div className="w-16 h-3 rounded skeleton mb-1" />
                  <div className="w-12 h-2.5 rounded skeleton" />
                </div>
                <div className="w-10 h-3 rounded skeleton" />
              </div>
            ))
          ) : hotTickers.length === 0 ? (
            <div className="px-4 py-6 text-center">
              <p className="text-sm font-medium" style={{ color: 'var(--text-secondary)' }}>{t('Not enough activity yet')}</p>
              <p className="text-xs mt-1" style={{ color: 'var(--text-muted)' }}>{t('The most-traded tickers appear as trades come in.')}</p>
            </div>
          ) : (
            hotTickers.map((ticker, i) => (
              <button
                key={ticker.symbol}
                className="w-full flex items-center justify-between px-4 py-3 transition-colors duration-100"
                onMouseEnter={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'var(--bg-card-hover)'; }}
                onMouseLeave={(e) => { (e.currentTarget as HTMLButtonElement).style.background = 'transparent'; }}
              >
                <div className="flex items-center gap-3">
                  <span className="font-mono-nums w-4 text-right" style={{ fontSize: 12, color: 'var(--text-muted)' }}>{i + 1}</span>
                  <div className="text-left">
                    <div className="font-mono-nums font-bold" style={{ fontSize: '13.5px', color: 'var(--text-primary)' }}>{ticker.symbol}</div>
                    <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{ticker.label}</div>
                  </div>
                </div>
                <div className="flex items-center gap-1.5">
                  {ticker.direction === 'up'
                    ? <TrendingUp size={12} style={{ color: 'var(--bullish)' }} />
                    : <TrendingDown size={12} style={{ color: 'var(--bearish)' }} />}
                  <span className="font-mono-nums font-bold" style={{ fontSize: 12, color: ticker.direction === 'up' ? 'var(--bullish)' : 'var(--bearish)' }}>
                    {ticker.direction === 'up' ? t('Bullish') : t('Bearish')}
                  </span>
                </div>
              </button>
            ))
          )}
        </div>
      </div>

      {/* ── TOP TRADERS THIS WEEK ("Top trejderi nedelje" widget; eligibleForTop + stmScore, 7d) ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <Zap size={14} style={{ color: 'var(--accent)' }} />
          <SectionLabel>{t('Top Traders This Week')}</SectionLabel>
        </div>
        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {renderTraders(weekTraders)}
        </div>
        <div className="px-4 py-3 border-t" style={{ borderColor: 'var(--border)' }}>
          <Link href="/leaderboard" className="w-full flex items-center justify-center gap-1.5 transition-colors duration-100" style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)' }}>
            {t('View full leaderboard')} →
          </Link>
        </div>
      </div>

      {/* ── TOP TRADERS THIS MONTH (eligibleForTop + stmScore, 30d) ── */}
      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border)', borderRadius: 16, overflow: 'hidden' }}>
        <div className="flex items-center gap-2 px-4 py-3 border-b" style={{ borderColor: 'var(--border)' }}>
          <Trophy size={14} style={{ color: 'var(--win)' }} />
          <SectionLabel>{t('Top Traders This Month')}</SectionLabel>
        </div>
        <div className="divide-y" style={{ borderColor: 'var(--border-subtle)' }}>
          {renderTraders(monthTraders)}
        </div>
      </div>

    </aside>
  );
}
